/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    object, bool, func, number, arrayOf, shape, string,
} from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import Media from 'react-media';
import { compose } from 'recompose';
import withPageView from '../../../../helpers/tracking/hocs/withPageView';
import Loading from '../../common/Loading';
import EventDispatcher from '../../state/reducers/EventDispatcher';
import CUSTOM_BREAKPOINTS from '../../style/customBreakpoints';

import BillingInfo from './BillingInfo';
import WalletInfo from './WalletInfo';
import configENV from '../../config';
import DesktopBillingInfo from './DesktopBillingAddress/DesktopBillingInfo';
import { getFeatureFlags } from '../../utils/featureSelectors';
import { trackPage } from '../../../../helpers/tracking/common/commonTrackingHelpers';
import GraphqlSeoHeadTags from '../../../../components/GraphqlComponents/GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import AccountHome from '../MyAccount/AccountHome';

const { PERSIST_KEY } = configENV;

class BillingAddress extends React.Component {
    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount = () => {
        const { billingAddress, wallet, fetchBillingAddress } = this.props;
        fetchBillingAddress(billingAddress, wallet);
    };

    render() {
        // eslint-disable-next-line object-curly-newline
        const {
            isFetchingModal,
            billingAddress,
            userProfile,
            featureFlags,
            wallet,
            countries,
            cardTypes,
            breakpoint,
        } = this.props;
        const {
            handleClickEditBillingAddress,
            handleClickAddNewCreditCard,
            handleClickEditCreditCard,
            handleClickRemoveCreditCard,
            handleSetDefaultCard,
        } = this.props;
        const headTags = (
            <GraphqlSeoHeadTags seoData={{
                page_title: 'Payment &amp; Billing',
                location: { pathname: '/account/address-book' },
            }}
            />
        );
        if (!billingAddress || isFetchingModal) {
            return (
                <>
                    {headTags}
                    <Loading pageTitle="Payment &amp; Billing" />;
                </>
            );
        }
        const breakpointExpr =  `(max-width: ${(breakpoint || CUSTOM_BREAKPOINTS.tabLandscape)}px)`;
        return (
            <>
                {headTags}
                <Media query={breakpointExpr}>
                    {(matches) => (
                        matches ? (
                            <>
                                <h1>
                                    <AccountHome /> Payment &amp; Billing
                                </h1>
                                <>
                                    <BillingInfo
                                        billingAddress={billingAddress}
                                        countries={countries}
                                        onEdit={handleClickEditBillingAddress}
                                    />
                                    <WalletInfo
                                        cardTypes={cardTypes}
                                        wallet={wallet}
                                        onAddCard={handleClickAddNewCreditCard}
                                        onEditCard={handleClickEditCreditCard}
                                        onRemoveCard={handleClickRemoveCreditCard}
                                        onSetDefaultCard={handleSetDefaultCard}
                                    />
                                </>
                            </>
                        ) : (
                            <>
                                <DesktopBillingInfo
                                    userProfile={userProfile}
                                    featureFlags={featureFlags}
                                    billingAddress={billingAddress}
                                    countries={countries}
                                    cardTypes={cardTypes}
                                    wallet={wallet}
                                    onEdit={handleClickEditBillingAddress}
                                    onAddCard={handleClickAddNewCreditCard}
                                    onEditCard={handleClickEditCreditCard}
                                    onSetDefaultCard={handleSetDefaultCard}
                                />
                            </>
                        ))}
                </Media>
            </>
        );
    }
}

BillingAddress.defaultProps = {
    userProfile: {},
    featureFlags: {},
    billingAddress: [],
    wallet: [],
    countries: [],
    cardTypes: [],
    breakpoint: CUSTOM_BREAKPOINTS.tabLandscape,
};

BillingAddress.propTypes = {
    isFetchingModal: bool.isRequired,
    billingAddress: arrayOf(
        shape({
            AddressBookEntryId: string,
            ContactId: number,
            FirstName: string,
            LastName: string,
            RelationShip: string,
            NickName: string,
            LastUpdateDate: string,
            PhoneNumber: string,
            EmployerName: string,
            NumberOfAddresses: number,
            Addresses: arrayOf(
                shape({
                    AddressId: number,
                    AddressBookEntryId: string,
                    AddressType: string,
                    AddressLineOne: string,
                    AddressLineTwo: string,
                    City: string,
                    StateProvince: string,
                    PostalCode: string,
                    Country: string,
                    IsPartialMatch: bool,
                    IsUnknownAddress: bool,
                    Latitude_degrees: number,
                    Longitude_degrees: number,
                    IsBillingAddress: bool,
                    ContactId: number,
                }),
            ),
        }),
    ),
    userProfile: shape({
        userRole: string,
    }),
    featureFlags: object,
    wallet: arrayOf(
        shape({
            walletId: string.isRequired,
            cardType: string.isRequired,
            payType: string.isRequired,
            cardHolderName: string.isRequired,
            cardNumber: string.isRequired,
            lastFourDigits: string.isRequired,
            expiryDate: string.isRequired,
            status: string,
            subsIndicator: string,
        }),
    ),
    countries: arrayOf(
        shape({
            id: string,
            name: string,
        }),
    ),
    cardTypes: arrayOf(
        shape({
            id: string,
            name: string,
        }),
    ),
    breakpoint: number,
    fetchBillingAddress: func.isRequired,
    handleClickEditBillingAddress: func.isRequired,
    handleClickAddNewCreditCard: func.isRequired,
    handleClickEditCreditCard: func.isRequired,
    handleClickRemoveCreditCard: func.isRequired,
    handleSetDefaultCard: func.isRequired,
};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    let featureFlags = {};
    if (state.app) {
        try {
            featureFlags = {
                ...getFeatureFlags(state),
            };
            // eslint-disable-next-line no-empty
        } catch (ex) { }
    }
    return {
        isFetchingModal: newState.isFetchingModal,
        toggleState: newState.toggleState,
        billingAddress: newState.billingAddress,
        wallet: newState.wallet,
        countries: newState.countries,
        cardTypes: newState.cardtypes,
        userProfile: newState.userProfile,
        featureFlags,
    };
};

const mapDispatchToProps = (dispatch) => ({
    fetchBillingAddress: (billingAddress, wallet) => dispatch(EventDispatcher.fetchBillingAddress(billingAddress, wallet)),
    handleClickEditBillingAddress: (address) => dispatch(EventDispatcher.handleClickEditBillingAddress(address)),
    handleClickAddNewCreditCard: () => dispatch(EventDispatcher.handleClickAddNewCreditCard()),
    handleClickRemoveCreditCard: (walletId) => dispatch(EventDispatcher.handleClickRemoveCreditCard(walletId)),
    handleClickEditCreditCard: (walletId) => dispatch(EventDispatcher.handleClickEditCreditCard(walletId)),
    handleSetDefaultCard: (walletId) => dispatch(EventDispatcher.handleSetDefaultCard(walletId)),
});

const enhance = compose(
    trackPage({
        type: 'billingaddress',
        name: 'MyAccount',
        accountPage: true,
    }),
    connect(
        mapStateToProps,
        mapDispatchToProps,
    ),
    withRouter,
    withPageView({
        pageViewSuccessTest: () => true,
    }),
);
export default enhance(BillingAddress);
